/**
 * API client for tenant portal endpoints. Attaches Auth0 access token to requests.
 * Uses relative /api when VITE_API_URL is unset (Vite proxy to tenant-server).
 */
import { API_BASE_URL } from '../config/api';
import {
  isPortalAuthFailureResponse,
  notifyTenantPortalAuthFailure,
  shouldSkipPortalSessionInvalidation,
} from './tenantPortalSession';
import type { ApplicationTaskChecks } from '../utils/application-progress';

export interface TenantLeaseInfo {
  unitNumber: string | null;
  propertyName: string | null;
  address: string | null;
  status: string | null;
  leaseFrom: string | null;
  leaseTo: string | null;
  propertyUnitCount: number;
  isByTheBed?: boolean;
  monthlyRent?: number | null;
}

export interface TenantLeaseRecord {
  leaseId: string;
  tenantId: number;
  tenantLeaseStatus: string;
  leaseStatus: string | null;
  leaseType: string | null;
  unitNumber: string | null;
  propertyName: string | null;
  address: string | null;
  leaseFromDate: string | null;
  leaseToDate: string | null;
  moveInDate: string | null;
}

export interface ParkingLeaseInfo {
  unitNumber: string | null;
  propertyName: string | null;
  status: string | null;
}

export interface MoveOutInfo {
  id: string;
  status: string;
  appointmentStartTime: string | null;
  appointmentEndTime: string | null;
  leaseEndDate: string;
  /** False when within 72h before noon ET on lease end — portal cannot book or reschedule */
  bookingSelfServiceAvailable?: boolean;
  furnitureSaleSellerConsent: boolean;
  furnitureSaleProofFilename: string | null;
  furnitureSaleProofMimeType: string | null;
  furnitureSaleProofSize: number | null;
  askForGoogleReview: boolean;
  checklistTotal: number;
  checklistCompleted: number;
}

export interface SlotInfo {
  startTime: string;
  endTime: string;
  capacity: number;
  booked: number;
  available: number;
}

export interface MoveInInfo {
  id: string;
  unitId: string;
  propertyId: string;
  status: string;
  scheduledDate: string | null;
  appointmentStartTime: string | null;
  appointmentEndTime: string | null;
  leaseStartDate: string | null;
  leaseEndDate: string | null;
  checklistTotal: number;
  checklistCompleted: number;
  isByTheBed?: boolean;
  showUtilitiesSection?: boolean;
  showRentSplitSection?: boolean;
  requiresCumEnrollmentForm?: boolean;
  portalIsHouseManager?: boolean;
  monthlyRent?: number | null;
}

export interface MoveInSlotInfo {
  startTime: string;
  endTime: string;
  capacity: number;
  booked: number;
  available: number;
}

export interface MoveInChecklistItem {
  id: string;
  moveInId: string;
  type: string;
  itemKey: string;
  label: string;
  category: string | null;
  completed: boolean;
  completedAt: string | null;
  completedByTenantId: number | null;
  metadata: unknown;
  sortOrder: number;
}

export interface MoveInTenantInsurance {
  tenantId: number;
  firstName: string;
  lastName: string;
  isHouseManager: boolean;
  insuranceConfirmed: boolean;
  hasInsuranceDocument: boolean;
  insurancePolicyExpiry: string | null;
  isCurrentUser?: boolean;
  documentUrl?: string | null;
  documentFilename?: string | null;
}

export interface MoveInRentSplitData {
  tenants: Array<{
    tenantId: number;
    firstName: string;
    lastName: string;
    isHouseManager: boolean;
  }>;
  totalRent: number | null;
  bedrooms: number | null;
  rentSplitData: Array<{ tenantId: number; amount: number }> | null;
}

export interface MoveInUnitRoommate {
  bedLabel: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  program: string | null;
  yearOfStudy: string | null;
  isCurrentUser: boolean;
}

export interface MoveInUnitRoommatesData {
  unitNumber: string | null;
  roommates: MoveInUnitRoommate[];
}

export interface MoveInUtilitiesStatus {
  houseManagerTenantId: number | null;
  houseManagerName: string | null;
  utilitiesAccountConfirmed: boolean;
  hasUtilitiesDocument: boolean;
  documentUrl?: string | null;
  documentFilename?: string | null;
  requiresCumEnrollmentForm?: boolean;
  hasEnrollmentFormDocument?: boolean;
  enrollmentFormDocumentUrl?: string | null;
  enrollmentFormDocumentFilename?: string | null;
}

export interface TenantMeData {
  kind?: 'tenant';
  tenantId: number;
  firstName: string;
  lastName: string;
  phone: string | null;
  residentialLease: TenantLeaseInfo | null;
  parkingLease: ParkingLeaseInfo | null;
  leases: TenantLeaseRecord[];
  moveOut: MoveOutInfo | null;
  moveIn: MoveInInfo | null;
  hasMultipleActiveLeases: boolean;
}

export interface ApplicantMeData {
  kind: 'applicant';
  personId: string | null;
  applicantId: string;
  applicationId: string | null;
  displayName: string | null;
  phone: string;
  email: string | null;
}

export type ProspectGroupPortalMemberStatus = 'DRAFT' | 'INVITED' | 'ACCEPTED' | 'REMOVED';

export interface ProspectGroupPortalMember {
  id: string;
  personId: string;
  prospectId: string | null;
  displayName: string;
  primaryPhone: string | null;
  phones?: string[];
  primaryEmail: string | null;
  status: ProspectGroupPortalMemberStatus;
  isHostManager: boolean;
}

export interface ProspectGroupPortalDraftInviteRow {
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  canadianPhone?: string;
  clientId?: string;
}

export interface GroupProductPreferenceSide {
  values: {
    bedrooms: string[];
    bathrooms: string[];
    laundry: 'in_unit' | 'in_building' | 'either' | null;
    parking: 'required' | 'preferred' | 'not_needed' | null;
    furnished: 'furnished' | 'unfurnished' | 'either' | null;
    enSuiteBathroom: 'required' | 'preferred' | 'not_needed' | null;
    outdoorSpace: 'small' | 'medium' | 'large' | 'not_applicable' | null;
    rentPerBedroom: { min: number | null; max: number | null };
    location: { zones: Array<{ key: string; subzones: string[] }> };
  };
  priority: string[];
}

export interface ProspectGroupPortalData {
  id: string;
  name: string;
  status: 'DRAFT' | 'ACTIVE' | 'EXITED';
  isHostManager: boolean;
  canEditDraft?: boolean;
  hostManagerPersonId: string | null;
  createdByPersonId?: string | null;
  members: ProspectGroupPortalMember[];
  draftInviteRows?: ProspectGroupPortalDraftInviteRow[];
  productPreferences?: { stated: GroupProductPreferenceSide } | null;
  finalizedAt: string | null;
  soloDeclaredAt?: string | null;
}

export interface ProspectMeData {
  kind: 'prospect';
  personId: string;
  prospectId: string;
  displayName: string | null;
  phone: string | null;
  email: string | null;
  verifiedVia: 'phone' | 'email';
  profileComplete: boolean;
  primaryBedroom: string | null;
  secondaryBedroom: string | null;
  tertiaryBedroom: string | null;
  generalNotifications: boolean;
  schoolYear: string | null;
  school: string | null;
  program: string | null;
  expectedGraduationTerm: string | null;
  expectedGraduationYear: string | null;
  created: boolean;
  prospectGroup?: ProspectGroupPortalData | null;
  referralName?: string | null;
  referralSource?: string | null;
}

export type PortalMeData = (TenantMeData & { kind: 'tenant' }) | ApplicantMeData | ProspectMeData;

export interface TenantMeResponse {
  success: boolean;
  data: PortalMeData;
}

/** Thrown when /tenant/me returns 403 TENANT_NOT_FOUND (phone not in system). */
export class TenantNotFoundError extends Error {
  readonly code = 'TENANT_NOT_FOUND';
  constructor(message: string) {
    super(message);
    this.name = 'TenantNotFoundError';
  }
}

/** Thrown when /tenant/me returns 401 (invalid or missing token). User must sign out and try again. */
export class TenantUnauthorizedError extends Error {
  readonly code = 'UNAUTHORIZED';
  constructor(message: string) {
    super(message);
    this.name = 'TenantUnauthorizedError';
  }
}

async function maybeInvalidatePortalSessionFromResponse(
  endpoint: string,
  res: Response
): Promise<void> {
  if (shouldSkipPortalSessionInvalidation(endpoint) || res.ok) return;
  let errorCode: string | null = null;
  try {
    const cloned = res.clone();
    const body = (await cloned.json()) as { error?: string };
    errorCode = typeof body.error === 'string' ? body.error : null;
  } catch {
    // Non-JSON error body — status-only check below.
  }
  if (!isPortalAuthFailureResponse(res.status, errorCode)) return;
  if (res.status === 503) {
    notifyTenantPortalAuthFailure('http_service_unavailable');
    return;
  }
  if (res.status === 403) {
    notifyTenantPortalAuthFailure('http_forbidden');
    return;
  }
  notifyTenantPortalAuthFailure('http_unauthorized');
}

export async function tenantFetch(
  endpoint: string,
  getToken: () => Promise<string>,
  options?: RequestInit
): Promise<Response> {
  let token: string;
  try {
    token = await getToken();
  } catch {
    notifyTenantPortalAuthFailure('access_token_unavailable');
    throw new Error('Unable to obtain access token');
  }
  const url = `${API_BASE_URL}${endpoint}`;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });
  await maybeInvalidatePortalSessionFromResponse(endpoint, res);
  return res;
}

export async function getTenantMe(
  getToken: () => Promise<string>
): Promise<TenantMeResponse> {
  const res = await tenantFetch('/tenant/me', getToken);
  const data = await res.json();
  if (!res.ok) {
    const message = data.message || data.error || `HTTP ${res.status}`;
    if (res.status === 403 && (data.error === 'TENANT_NOT_FOUND' || data.error === 'IDENTITY_NOT_RESOLVED')) {
      throw new TenantNotFoundError(message);
    }
    if (res.status === 401) {
      throw new TenantUnauthorizedError(message);
    }
    throw new Error(message);
  }
  if (data?.data && !data.data.kind && data.data.tenantId != null) {
    data.data.kind = 'tenant';
  }
  return data;
}

export async function submitProspectPreApplication(
  getToken: () => Promise<string>,
  payload: {
    name: string;
    email?: string;
    phone?: string;
    bedroomRankings: string[];
    generalNotifications: boolean;
    schoolYear: string;
    school: string;
    program: string;
    expectedGraduationTerm: string;
    expectedGraduationYear: string;
    referrerName?: string;
    referralName?: string;
    referrerSource?: string;
    referralSource?: string;
  },
  options?: { keepalive?: boolean },
): Promise<ProspectMeData> {
  const res = await tenantFetch('/tenant/prospect/pre-application', getToken, {
    method: 'POST',
    body: JSON.stringify(payload),
    keepalive: options?.keepalive,
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data as ProspectMeData;
}

export class ProspectGroupApiError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
    public readonly phoneE164?: string,
  ) {
    super(message);
    this.name = 'ProspectGroupApiError';
  }
}

async function parseProspectGroupError(
  res: Response,
  data: { message?: string; error?: string; phoneE164?: string },
): Promise<never> {
  const message =
    data.message ||
    (data.error === 'VALIDATION_ERROR'
      ? 'Could not save your group. Check names and phone numbers, then try again.'
      : data.error === 'PHONE_ALREADY_ON_FILE_CONTACT_SUPPORT'
      ? 'This phone number is already on file. Please contact support.'
      : data.error === 'PERSON_ALREADY_IN_ANOTHER_GROUP'
        ? 'This person is already in another group and cannot be added.'
        : data.error === 'ALREADY_IN_GROUP'
          ? 'This person is already in this group.'
          : data.error || `HTTP ${res.status}`);
  throw new ProspectGroupApiError(message, data.error, data.phoneE164);
}

export async function fetchProspectGroup(
  getToken: () => Promise<string>,
): Promise<ProspectGroupPortalData> {
  const res = await tenantFetch('/tenant/prospect-group', getToken);
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  return data.data as ProspectGroupPortalData;
}

export async function applyWebsiteHiveProspectIntent(
  getToken: () => Promise<string>,
): Promise<{ applied: boolean; groupId: string | null; consumed: boolean }> {
  const res = await tenantFetch('/tenant/prospect-group/website-hive-intent', getToken, {
    method: 'POST',
  });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  return (data.data ?? { applied: false, groupId: null, consumed: false }) as {
    applied: boolean;
    groupId: string | null;
    consumed: boolean;
  };
}

export interface ProspectLeaseSigningSigner {
  name: string;
  signed: boolean;
  role?: 'tenant' | 'amberPeak';
  personId?: string | null;
}

export interface ProspectLeaseSigningStatus {
  leaseSent: boolean;
  signedPersonIds: string[];
  signers?: ProspectLeaseSigningSigner[];
}

export async function fetchProspectLeaseSigning(
  getToken: () => Promise<string>,
): Promise<ProspectLeaseSigningStatus> {
  const res = await tenantFetch('/tenant/prospect/lease-signing', getToken);
  const data = await res.json() as { data?: ProspectLeaseSigningStatus; message?: string };
  if (!res.ok) {
    throw new Error(data.message || 'Unable to load lease signing.');
  }
  return data.data ?? { leaseSent: false, signedPersonIds: [], signers: [] };
}

export type ProspectClosingArtifactKey =
  | 'UTILITY_SETUP_CONFIRMATION'
  | 'HOUSEMATE_AGREEMENT'
  | 'VOID_CHEQUE'
  | 'PAD'
  | 'LAST_MONTH_RENT_E_TRANSFER_SENT';

export interface ProspectClosingReviewDto {
  status: 'PENDING' | 'APPROVED' | 'REVIEW_FEEDBACK';
  latestFeedbackMessage: string | null;
}

export interface ProspectClosingChecklistMemberDto {
  personId: string;
  displayName: string;
}

export interface UtilitiesKingstonSetupContextDto {
  tenantDisplayName: string;
  propertyAddress: string | null;
  leaseStartDateLabel: string | null;
  utilityServiceLabels: string[];
}

export interface ProspectClosingChecklistDto {
  members: ProspectClosingChecklistMemberDto[];
  group: {
    leaseSent: boolean;
    signedByPersonId: Record<string, boolean>;
    leaseFileName: string | null;
    leaseDocumentId: string | null;
    utilityFileName: string | null;
    utilityDocumentId: string | null;
    housemateSignedFileName: string | null;
    housemateDocumentId: string | null;
    housemateTemplateReady: boolean;
    utilitiesKingston: UtilitiesKingstonSetupContextDto;
    reviews: {
      leaseSigning: ProspectClosingReviewDto;
      utilitySetup: ProspectClosingReviewDto;
      housemateAgreement: ProspectClosingReviewDto;
    };
  };
  personal: Record<
    string,
    {
      applicantId: string;
      headshotDocumentId: string | null;
      padFileName: string | null;
      padDocumentId: string | null;
      padSigned: boolean;
      voidChequeFileName: string | null;
      voidChequeDocumentId: string | null;
      lastMonthRentETransferFileName: string | null;
      lastMonthRentETransferDocumentId: string | null;
      lastMonthRentETransferSent: boolean;
      lastMonthRentETransferReceived: boolean;
      eTransferAmountLabel: string | null;
      reviews: {
        pad: ProspectClosingReviewDto;
        voidCheque: ProspectClosingReviewDto;
        lastMonthRentETransferSent: ProspectClosingReviewDto;
        lastMonthRentETransferReceived: ProspectClosingReviewDto;
      };
    }
  >;
}

export async function createProspectPadLinkToken(getToken: () => Promise<string>): Promise<string> {
  const res = await tenantFetch('/tenant/prospect/closing/pad/link-token', getToken, { method: 'POST' });
  const data = await res.json() as { data?: { linkToken?: string }; message?: string };
  if (!res.ok || !data.data?.linkToken) {
    throw new Error(data.message || 'Unable to start bank connection.');
  }
  return data.data.linkToken;
}

export async function sendBlankProspectPadAgreement(
  getToken: () => Promise<string>,
): Promise<ProspectClosingChecklistDto> {
  const res = await tenantFetch('/tenant/prospect/closing/pad/send-blank', getToken, { method: 'POST' });
  const data = await res.json() as { data?: ProspectClosingChecklistDto; message?: string };
  if (!res.ok || !data.data) {
    throw new Error(data.message || 'Unable to send the PAD agreement.');
  }
  return data.data;
}

export async function exchangeProspectPadPublicToken(
  getToken: () => Promise<string>,
  publicToken: string,
): Promise<ProspectClosingChecklistDto> {
  const res = await tenantFetch('/tenant/prospect/closing/pad/exchange', getToken, {
    method: 'POST',
    body: JSON.stringify({ publicToken }),
  });
  const data = await res.json() as { data?: ProspectClosingChecklistDto; message?: string };
  if (!res.ok || !data.data) {
    throw new Error(data.message || 'Unable to send the PAD agreement.');
  }
  return data.data;
}

export async function fetchProspectClosingChecklist(
  getToken: () => Promise<string>,
): Promise<ProspectClosingChecklistDto> {
  const res = await tenantFetch('/tenant/prospect/closing-checklist', getToken);
  const data = await res.json() as { data?: ProspectClosingChecklistDto; message?: string };
  if (!res.ok) {
    throw new Error(data.message || 'Unable to load closing checklist.');
  }
  if (!data.data) throw new Error('Unable to load closing checklist.');
  return data.data;
}

export async function uploadProspectClosingFile(
  getToken: () => Promise<string>,
  file: File,
  artifactKey: ProspectClosingArtifactKey,
): Promise<ProspectClosingChecklistDto> {
  const start = await tenantFetch('/tenant/prospect/closing/upload-url', getToken, {
    method: 'POST',
    body: JSON.stringify({
      artifactKey,
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
    }),
  });
  const started = await start.json();
  if (!start.ok) throw new Error(started.message || started.error || 'Unable to start that upload.');
  const uploaded = await fetch(started.data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!uploaded.ok) throw new Error('The file did not upload.');
  const saved = await tenantFetch('/tenant/prospect/closing/documents', getToken, {
    method: 'POST',
    body: JSON.stringify({
      artifactKey,
      s3Key: started.data.s3Key,
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
      size: file.size,
    }),
  });
  const savedBody = await saved.json();
  if (!saved.ok) throw new Error(savedBody.message || savedBody.error || 'Unable to save that file.');
  return savedBody.data as ProspectClosingChecklistDto;
}

export async function acknowledgeProspectLastMonthRentETransferSent(
  getToken: () => Promise<string>,
): Promise<ProspectClosingChecklistDto> {
  const res = await tenantFetch('/tenant/prospect/closing/last-month-rent-e-transfer-sent', getToken, { method: 'POST' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Unable to save that step.');
  return data.data as ProspectClosingChecklistDto;
}

export async function clearProspectLastMonthRentETransferSent(
  getToken: () => Promise<string>,
): Promise<ProspectClosingChecklistDto> {
  const res = await tenantFetch('/tenant/prospect/closing/last-month-rent-e-transfer-sent', getToken, { method: 'DELETE' });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Unable to save that step.');
  return data.data as ProspectClosingChecklistDto;
}

export async function removeProspectClosingDocument(
  getToken: () => Promise<string>,
  artifactKey: ProspectClosingArtifactKey,
): Promise<ProspectClosingChecklistDto> {
  const res = await tenantFetch(
    `/tenant/prospect/closing/documents/${encodeURIComponent(artifactKey)}`,
    getToken,
    { method: 'DELETE' },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Unable to delete that file.');
  return data.data as ProspectClosingChecklistDto;
}

export async function putProspectGroupProductPreferences(
  getToken: () => Promise<string>,
  stated: GroupProductPreferenceSide,
): Promise<ProspectGroupPortalData> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const res = await tenantFetch('/tenant/prospect-group/product-preferences', getToken, {
        method: 'PUT',
        body: JSON.stringify({ stated }),
      });
      const text = await res.text();
      if (!text.trim()) {
        throw new Error('The save did not finish. Try again.');
      }
      let data: { message?: string; error?: string; phoneE164?: string; data?: ProspectGroupPortalData };
      try {
        data = JSON.parse(text) as typeof data;
      } catch {
        throw new Error('The save did not finish. Try again.');
      }
      if (!res.ok) {
        if (data.error === 'VALIDATION_ERROR') {
          throw new ProspectGroupApiError(
            data.message || 'Could not save group product preferences.',
            data.error,
          );
        }
        await parseProspectGroupError(res, data);
      }
      if (!data.data) {
        throw new Error('The save did not finish. Try again.');
      }
      return data.data;
    } catch (err) {
      lastError = err;
      const message = err instanceof Error ? err.message : '';
      const retryable = message === 'The save did not finish. Try again.' || err instanceof TypeError;
      if (!retryable || attempt === 1) {
        throw err instanceof Error ? err : new Error('Could not save group product preferences.');
      }
      await new Promise((resolve) => window.setTimeout(resolve, 500));
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Could not save group product preferences.');
}

export async function putProspectGroupSoloDeclared(
  getToken: () => Promise<string>,
  declared: boolean,
): Promise<ProspectGroupPortalData> {
  const res = await tenantFetch('/tenant/prospect-group/solo', getToken, {
    method: 'PUT',
    body: JSON.stringify({ declared }),
  });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  return data.data as ProspectGroupPortalData;
}

export async function putProspectGroupDraft(
  getToken: () => Promise<string>,
  payload: {
    inviteRows: ProspectGroupPortalDraftInviteRow[];
  },
  options?: { keepalive?: boolean },
): Promise<{ group: ProspectGroupPortalData; inviteEmailError: string | null; emailInviteSent: boolean }> {
  const res = await tenantFetch('/tenant/prospect-group/draft', getToken, {
    method: 'PUT',
    body: JSON.stringify(payload),
    keepalive: options?.keepalive,
  });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  const inviteEmail = data.inviteEmail as { sent?: boolean; error?: string } | undefined;
  return {
    group: data.data as ProspectGroupPortalData,
    emailInviteSent: inviteEmail?.sent === true,
    inviteEmailError: inviteEmail && inviteEmail.sent === false
      ? inviteEmail.error || 'Could not email that invite.'
      : null,
  };
}

export async function putProspectGroupMemberEmail(
  getToken: () => Promise<string>,
  memberId: string,
  email: string,
): Promise<{ group: ProspectGroupPortalData; inviteEmailError: string | null }> {
  const res = await tenantFetch(`/tenant/prospect-group/members/${encodeURIComponent(memberId)}/email`, getToken, {
    method: 'PUT',
    body: JSON.stringify({ email }),
  });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  const inviteEmail = data.inviteEmail as { sent?: boolean; error?: string } | undefined;
  return {
    group: data.data as ProspectGroupPortalData,
    inviteEmailError: inviteEmail && inviteEmail.sent === false
      ? inviteEmail.error || 'Could not email that invite.'
      : null,
  };
}

export async function startPhoneVerification(
  getToken: () => Promise<string>,
  phone: string,
): Promise<void> {
  const res = await tenantFetch('/tenant/phone/verification', getToken, {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      (data as { message?: string }).message || 'We could not text that number. Check it and try again.',
    );
  }
}

export async function confirmPhoneVerification(
  getToken: () => Promise<string>,
  phone: string,
  code: string,
): Promise<string> {
  const res = await tenantFetch('/tenant/phone/verification/check', getToken, {
    method: 'POST',
    body: JSON.stringify({ phone, code }),
  });
  const data = (await res.json().catch(() => ({}))) as { message?: string; data?: { phone?: string } };
  if (!res.ok) {
    throw new Error(data.message || 'That code is wrong or expired. Request a new one.');
  }
  return data.data?.phone ?? phone;
}

export async function putProspectGroupMemberPhone(
  getToken: () => Promise<string>,
  memberId: string,
  phone: string,
): Promise<ProspectGroupPortalData> {
  const res = await tenantFetch(`/tenant/prospect-group/members/${encodeURIComponent(memberId)}/phone`, getToken, {
    method: 'PUT',
    body: JSON.stringify({ phone }),
  });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  return data.data as ProspectGroupPortalData;
}

export async function setProspectGroupLeadContact(
  getToken: () => Promise<string>,
  personId: string,
): Promise<ProspectGroupPortalData> {
  const res = await tenantFetch('/tenant/prospect-group/lead', getToken, {
    method: 'POST',
    body: JSON.stringify({ personId }),
  });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  return data.data as ProspectGroupPortalData;
}

export async function finalizeProspectGroup(
  getToken: () => Promise<string>,
): Promise<ProspectGroupPortalData> {
  const res = await tenantFetch('/tenant/prospect-group/finalize', getToken, { method: 'POST' });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  return data.data as ProspectGroupPortalData;
}

export async function leaveProspectGroup(
  getToken: () => Promise<string>,
  successorPersonId?: string,
): Promise<ProspectGroupPortalData> {
  const res = await tenantFetch('/tenant/prospect-group/leave', getToken, {
    method: 'POST',
    body: JSON.stringify(successorPersonId ? { successorPersonId } : {}),
  });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  return data.data as ProspectGroupPortalData;
}

export async function removeProspectGroupMember(
  getToken: () => Promise<string>,
  memberId: string,
): Promise<ProspectGroupPortalData> {
  const res = await tenantFetch(`/tenant/prospect-group/members/${encodeURIComponent(memberId)}`, getToken, {
    method: 'DELETE',
  });
  const data = await res.json();
  if (!res.ok) await parseProspectGroupError(res, data);
  return data.data as ProspectGroupPortalData;
}

export interface QualificationCallSlot {
  startAt: string;
  endAt: string;
  remaining: number;
}

export interface QualificationCallDay {
  date: string;
  label: string;
  slots: QualificationCallSlot[];
}

export interface QualificationCallAppointment {
  id: string;
  startAt: string;
  endAt: string;
  timezone: string;
  status: string;
}

export interface QualificationCallState {
  canBook: boolean;
  leadName: string | null;
  phoneDisplay: string | null;
  durationMinutes: number;
  timezone: string;
  appointment: QualificationCallAppointment | null;
  days: QualificationCallDay[];
  /** At least one discovery call for this household marked Showed (COMPLETED). */
  discoveryCallShowed: boolean;
}

export interface ListedUnitCard {
  id: string;
  title: string;
  propertyName: string;
  unitNumber: string;
  city: string | null;
  province: string | null;
  price: number;
  bedrooms: number;
  bathrooms: number;
  sqft: number | null;
  availableDate: string | null;
  imageUrl: string | null;
  photos: string[];
  listingUrl: string;
  /** Bedroom sizes this card matches. The Hive matches 1, 2, 3, and 4. */
  bedroomFilters: number[];
  /** The Hive as one building, rather than a single suite. */
  building: boolean;
}

export interface ShortlistUnitCard extends ListedUnitCard {
  rank: number;
}

export interface ProspectShortlistPage {
  groupId: string;
  groupName: string;
  shortlist: ShortlistUnitCard[];
  available: ListedUnitCard[];
}

async function readShortlistPage(res: Response): Promise<ProspectShortlistPage> {
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data as ProspectShortlistPage;
}

export async function getProspectShortlist(
  getToken: () => Promise<string>,
): Promise<ProspectShortlistPage> {
  const res = await tenantFetch('/tenant/prospect/shortlist', getToken);
  return readShortlistPage(res);
}

const shortlistImportsInFlight = new Map<string, Promise<ProspectShortlistPage>>();

export async function importProspectShortlistUnits(
  getToken: () => Promise<string>,
  unitIds: string[],
): Promise<ProspectShortlistPage> {
  const key = unitIds.join('\n');
  const inFlight = shortlistImportsInFlight.get(key);
  if (inFlight) return inFlight;

  const request = (async () => {
    const res = await tenantFetch('/tenant/prospect/shortlist/import', getToken, {
      method: 'POST',
      body: JSON.stringify({ unitIds }),
    });
    return readShortlistPage(res);
  })().finally(() => {
    shortlistImportsInFlight.delete(key);
  });
  shortlistImportsInFlight.set(key, request);
  return request;
}

export async function addProspectShortlistUnit(
  getToken: () => Promise<string>,
  unitId: string,
): Promise<ProspectShortlistPage> {
  const res = await tenantFetch('/tenant/prospect/shortlist', getToken, {
    method: 'POST',
    body: JSON.stringify({ unitId }),
  });
  return readShortlistPage(res);
}

export async function removeProspectShortlistUnit(
  getToken: () => Promise<string>,
  unitId: string,
): Promise<ProspectShortlistPage> {
  const res = await tenantFetch(`/tenant/prospect/shortlist/${encodeURIComponent(unitId)}`, getToken, {
    method: 'DELETE',
  });
  return readShortlistPage(res);
}

export async function reorderProspectShortlist(
  getToken: () => Promise<string>,
  unitIds: string[],
): Promise<ProspectShortlistPage> {
  const res = await tenantFetch('/tenant/prospect/shortlist/order', getToken, {
    method: 'PUT',
    body: JSON.stringify({ unitIds }),
  });
  return readShortlistPage(res);
}

export type PropertyTourSlotState = 'open' | 'taken' | 'yours' | 'busy' | 'discovery_required';

export interface PropertyTourSlot {
  startAt: string;
  endAt: string;
  state: PropertyTourSlotState;
  bookingId: string | null;
}

export interface PropertyTourBookableWindow {
  id: string;
  building: boolean;
  propertyId: string | null;
  unitId: string;
  unitNumber: string;
  propertyName: string;
  bedrooms: number;
  bathrooms: number;
  rank: number;
  startAt: string;
  endAt: string;
  imageUrl: string | null;
  photos: string[];
  slots: PropertyTourSlot[];
}

export interface ProspectTourHome {
  building: boolean;
  propertyId: string | null;
  unitId: string;
  unitNumber: string;
  propertyName: string;
  bedrooms: number;
  bathrooms: number;
  rank: number;
  imageUrl: string | null;
  photos: string[];
}

export interface CompletedProspectTour {
  bookingId: string;
  building: boolean;
  propertyId: string | null;
  unitId: string;
  unitNumber: string;
  propertyName: string;
  bedrooms: number;
  bathrooms: number;
  imageUrl: string | null;
  startAt: string;
  endAt: string;
}

export interface CancelledProspectTour extends CompletedProspectTour {
  cancellationNote: string | null;
}

export interface ProspectTourCalendarTextRecipient {
  personId: string;
  displayName: string;
}

export interface ProspectTourBookingPage {
  groupId: string;
  groupName: string;
  discoveryCallShowed: boolean;
  calendarTextRecipients: ProspectTourCalendarTextRecipient[];
  homes: ProspectTourHome[];
  windows: PropertyTourBookableWindow[];
  completedTours: CompletedProspectTour[];
  cancelledTours: CancelledProspectTour[];
}

async function readTourPage(res: Response): Promise<ProspectTourBookingPage> {
  const raw = await res.text();
  let data: { message?: string; error?: string; data?: ProspectTourBookingPage } = {};
  if (raw.trim()) {
    try {
      data = JSON.parse(raw) as typeof data;
    } catch {
      throw new Error(
        res.ok
          ? 'Tour data came back in an unexpected format. Refresh the page and try again.'
          : `HTTP ${res.status}`,
      );
    }
  } else if (!res.ok) {
    throw new Error(`HTTP ${res.status}. The server may still be restarting — refresh in a moment.`);
  }
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  const page = data.data as ProspectTourBookingPage;
  return {
    ...page,
    calendarTextRecipients: page.calendarTextRecipients ?? [],
  };
}

export class TourBookingRejectedError extends Error {
  readonly page: ProspectTourBookingPage | null;

  constructor(message: string, page: ProspectTourBookingPage | null) {
    super(message);
    this.name = 'TourBookingRejectedError';
    this.page = page;
  }
}

export async function getProspectTours(
  getToken: () => Promise<string>,
): Promise<ProspectTourBookingPage> {
  const res = await tenantFetch('/tenant/prospect/tours', getToken);
  return readTourPage(res);
}

export async function createProspectSchedulingConflict(
  getToken: () => Promise<string>,
  body: {
    unitId?: string;
    propertyId?: string;
    reason: 'NO_SLOTS_LEFT' | 'CANNOT_MAKE_OFFERED_TIME';
    reasonNote?: string;
    suggestions: Array<{ startAt: string; endAt: string }>;
  },
): Promise<void> {
  const res = await tenantFetch('/tenant/prospect/tours/scheduling-conflicts', getToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = (await res.json()) as { message?: string; error?: string };
  if (!res.ok) {
    throw new Error(data.message || data.error || 'Unable to save those times.');
  }
}

export async function bookProspectTour(
  getToken: () => Promise<string>,
  parentAppointmentId: string,
  startAt: string,
): Promise<ProspectTourBookingPage> {
  const res = await tenantFetch('/tenant/prospect/tours', getToken, {
    method: 'POST',
    body: JSON.stringify({ parentAppointmentId, startAt }),
  });
  const data = (await res.json()) as {
    message?: string;
    error?: string;
    data?: ProspectTourBookingPage | null;
  };
  if (res.status === 409) {
    throw new TourBookingRejectedError(
      data.message || 'That time was just taken. You did not get this appointment.',
      data.data ?? null,
    );
  }
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  if (!data.data) {
    throw new Error('Unable to book that tour.');
  }
  return data.data;
}

export async function cancelProspectTour(
  getToken: () => Promise<string>,
  bookingId: string,
  note: string,
): Promise<ProspectTourBookingPage> {
  const res = await tenantFetch(`/tenant/prospect/tours/${encodeURIComponent(bookingId)}/cancel`, getToken, {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
  return readTourPage(res);
}

export async function textProspectTourCalendar(
  getToken: () => Promise<string>,
  bookingIds: string[],
  audience: 'self' | 'group' = 'self',
): Promise<void> {
  const res = await tenantFetch('/tenant/prospect/tours/text-calendar', getToken, {
    method: 'POST',
    body: JSON.stringify({ bookingIds, audience }),
  });
  const data = (await res.json()) as { message?: string; error?: string };
  if (!res.ok) {
    throw new Error(
      data.message ||
        data.error ||
        'We could not send the calendar text. Try Download to save a .ics file on this device.',
    );
  }
}

export interface ProspectUnitAnalysisResponse {
  kitchen: number;
  livingRoom: number;
  bedrooms: number;
  bathrooms: number;
  laundry: number;
  location: number;
  vibes: number;
  phrases: string[];
}

export interface ProspectUnitAnalysisDraft {
  kitchen: number | null;
  livingRoom: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  laundry: number | null;
  location: number | null;
  vibes: number | null;
  phrases: string[];
}

export interface ProspectUnitAnalysisScoreAverages {
  kitchen: number | null;
  livingRoom: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  laundry: number | null;
  location: number | null;
  vibes: number | null;
}

export interface ProspectUnitAnalysisPhraseCount {
  phrase: string;
  count: number;
}

export interface ProspectUnitAnalysisMemberResult {
  personId: string;
  firstName: string;
  response: ProspectUnitAnalysisResponse;
}

export interface ProspectUnitAnalysisGroupSummary {
  submittedCount: number;
  averages: ProspectUnitAnalysisScoreAverages;
  phrases: ProspectUnitAnalysisPhraseCount[];
  members: ProspectUnitAnalysisMemberResult[];
}

export type ProspectUnitAnalysisAvailability = 'ready' | 'upcoming';

export interface ProspectUnitAnalysisUnit {
  unitId: string;
  unitNumber: string;
  propertyName: string;
  bedrooms: number;
  bathrooms: number;
  availability: ProspectUnitAnalysisAvailability;
  imageUrl: string | null;
  photos: string[];
  matterportUrl: string | null;
  canAnalyze: boolean;
  tourStartAt: string;
  mine: ProspectUnitAnalysisResponse | null;
  draft: ProspectUnitAnalysisDraft | null;
  group: ProspectUnitAnalysisGroupSummary | null;
}

export interface ProspectUnitAnalysisPage {
  groupId: string;
  groupName: string;
  units: ProspectUnitAnalysisUnit[];
}

async function readUnitAnalysisPage(res: Response): Promise<ProspectUnitAnalysisPage> {
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data as ProspectUnitAnalysisPage;
}

export async function getProspectUnitAnalyses(
  getToken: () => Promise<string>,
): Promise<ProspectUnitAnalysisPage> {
  const res = await tenantFetch('/tenant/prospect/unit-analyses', getToken);
  return readUnitAnalysisPage(res);
}

async function readSavedAck(res: Response): Promise<void> {
  const data = await res.json().catch(() => null) as { message?: string; error?: string } | null;
  if (!res.ok) throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
}

export async function saveProspectUnitAnalysis(
  getToken: () => Promise<string>,
  unitId: string,
  response: ProspectUnitAnalysisDraft,
  options?: { keepalive?: boolean },
): Promise<void> {
  const res = await tenantFetch(`/tenant/prospect/unit-analyses/${encodeURIComponent(unitId)}`, getToken, {
    method: 'PUT',
    body: JSON.stringify(response),
    keepalive: options?.keepalive,
  });
  await readSavedAck(res);
}

export async function clearProspectUnitAnalysis(
  getToken: () => Promise<string>,
  unitId: string,
  options?: { keepalive?: boolean },
): Promise<void> {
  const res = await tenantFetch(`/tenant/prospect/unit-analyses/${encodeURIComponent(unitId)}`, getToken, {
    method: 'DELETE',
    keepalive: options?.keepalive,
  });
  await readSavedAck(res);
}

export async function getQualificationCall(
  getToken: () => Promise<string>,
): Promise<QualificationCallState> {
  const res = await tenantFetch('/tenant/prospect/qualification-call', getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  const payload = data.data as QualificationCallState;
  return { ...payload, discoveryCallShowed: payload.discoveryCallShowed === true };
}

export async function reopenQualificationCallForRebook(
  getToken: () => Promise<string>,
): Promise<QualificationCallState> {
  const res = await tenantFetch('/tenant/prospect/qualification-call/rebook', getToken, {
    method: 'POST',
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  const payload = data.data as QualificationCallState;
  return { ...payload, discoveryCallShowed: payload.discoveryCallShowed === true };
}

export async function bookQualificationCall(
  getToken: () => Promise<string>,
  startAt: string,
): Promise<QualificationCallState> {
  const res = await tenantFetch('/tenant/prospect/qualification-call', getToken, {
    method: 'POST',
    body: JSON.stringify({ startAt }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  const payload = data.data as QualificationCallState;
  return { ...payload, discoveryCallShowed: payload.discoveryCallShowed === true };
}

export interface PortalContentEngagementRollup {
  firstWatchedAt: string | null;
  maxWatchPercent: number;
  maxWatchSeconds: number;
  totalWatchSeconds: number;
  videoDurationSeconds: number;
  cumulativeWatchPercent: number;
  watched: boolean;
  /** Cumulative playback time is at least half the video. Rewatches count. */
  watchedHalf: boolean;
}

export interface PortalContentPlayback {
  contentId: string;
  pageSlug: string;
  sectionName: string;
  contentType: string;
  mimeType: string;
  caption: string | null;
  altText: string | null;
  playbackUrl: string | null;
  engagement: PortalContentEngagementRollup;
}

export async function getPortalContentPlayback(
  getToken: () => Promise<string>,
  pageSlug: string,
  sectionName: string,
): Promise<PortalContentPlayback> {
  const res = await tenantFetch(
    `/tenant/portal-content/${encodeURIComponent(pageSlug)}/${encodeURIComponent(sectionName)}`,
    getToken,
  );
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data as PortalContentPlayback;
}

export async function recordPortalContentEvent(
  getToken: () => Promise<string>,
  pageSlug: string,
  sectionName: string,
  body: {
    eventType: 'PLAY' | 'PAUSE' | 'PROGRESS';
    watchPercent?: number;
    watchSeconds?: number;
    watchSecondsDelta?: number;
    videoDurationSeconds?: number;
  },
): Promise<{ recorded: boolean; engagement: PortalContentEngagementRollup }> {
  const res = await tenantFetch(
    `/tenant/portal-content/${encodeURIComponent(pageSlug)}/${encodeURIComponent(sectionName)}/events`,
    getToken,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data as { recorded: boolean; engagement: PortalContentEngagementRollup };
}

export async function notifyLoginEvent(
  getToken: () => Promise<string>
): Promise<void> {
  try {
    await tenantFetch('/tenant/login-event', getToken, { method: 'POST' });
  } catch {
    // Fire-and-forget — don't block login flow
  }
}

export async function sendPortalSessionHeartbeat(
  getToken: () => Promise<string>,
  body: { sessionId: string | null; path: string; active: boolean },
): Promise<{ sessionId: string | null; recorded: boolean }> {
  const res = await tenantFetch('/tenant/portal-session/heartbeat', getToken, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return {
    sessionId: typeof data.data?.sessionId === 'string' ? data.data.sessionId : null,
    recorded: data.data?.recorded === true,
  };
}

export async function getMoveOutSlots(
  getToken: () => Promise<string>,
  date: string,
  moveOutId?: string
): Promise<{ slots: SlotInfo[] }> {
  const base = moveOutId ? `/tenant/move-out/${moveOutId}/slots` : '/tenant/move-out/slots';
  const res = await tenantFetch(`${base}?date=${encodeURIComponent(date)}`, getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function getMoveOutSlotsForDates(
  getToken: () => Promise<string>,
  dates: string[],
  moveOutId?: string
): Promise<Record<string, SlotInfo[]>> {
  const base = moveOutId ? `/tenant/move-out/${moveOutId}/slots` : '/tenant/move-out/slots';
  const res = await tenantFetch(`${base}?dates=${dates.join(',')}`, getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data.slotsByDate;
}

export async function bookMoveOutSlot(
  getToken: () => Promise<string>,
  moveOutId: string,
  startTime: string
): Promise<MoveOutInfo> {
  const res = await tenantFetch('/tenant/move-out/book', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveOutId, startTime }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export interface MoveOutContentItem {
  id: string;
  sectionName: string;
  contentType: 'image' | 'video';
  cloudfrontUrl: string;
  mimeType: string;
  altText: string | null;
  caption: string | null;
  displayOrder: number;
}

export async function getMoveOutContent(
  getToken: () => Promise<string>
): Promise<MoveOutContentItem[]> {
  const res = await tenantFetch('/tenant/move-out/content', getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data;
}

export interface LedgerTransaction {
  id: number;
  date: string;
  type: string;
  totalAmount: number;
  memo: string | null;
  paymentMethod: string | null;
}

export interface LeaseLedgerData {
  transactions: LedgerTransaction[];
  totalCharges: number;
  totalPayments: number;
  netBalance: number;
}

export async function getMoveOutLedger(
  getToken: () => Promise<string>,
  moveOutId?: string
): Promise<LeaseLedgerData> {
  const path = moveOutId ? `/tenant/move-out/${moveOutId}/ledger` : '/tenant/move-out/ledger';
  const res = await tenantFetch(path, getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function requestDisposalService(
  getToken: () => Promise<string>,
  serviceType: 'furniture-removal' | 'garbage-pickup',
  preferredDate: string,
  moveOutId: string
): Promise<{
  sent: boolean;
  message?: string;
  workOrder?: {
    referenceNumber: number | null;
    appointmentScheduled: boolean;
    appointmentTime: string | null;
  } | null;
}> {
  const res = await tenantFetch('/tenant/move-out/request-service', getToken, {
    method: 'POST',
    body: JSON.stringify({ serviceType, preferredDate, moveOutId }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data;
}

export interface DisposalWorkOrder {
  id: string;
  referenceNumber: number | null;
  title: string | null;
  status: string | null;
  priority: string | null;
  createdAt: string;
  appointments: { startTime: string; endTime: string; status: string }[];
}

export async function getDisposalWorkOrders(
  getToken: () => Promise<string>,
  moveOutId: string
): Promise<DisposalWorkOrder[]> {
  const res = await tenantFetch(`/tenant/move-out/${moveOutId}/disposal-work-orders`, getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data ?? [];
}

export async function deleteDisposalWorkOrder(
  getToken: () => Promise<string>,
  workOrderId: string
): Promise<void> {
  const res = await tenantFetch(`/tenant/move-out/disposal-work-orders/${workOrderId}`, getToken, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
}

export async function requestReferenceLetter(
  getToken: () => Promise<string>,
  moveOutId: string
): Promise<{ success: boolean; requested: boolean }> {
  const res = await tenantFetch('/tenant/move-out/request-reference-letter', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveOutId }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data;
}

export async function submitMoveOutFeedback(
  getToken: () => Promise<string>,
  moveOutId: string,
  feedback: string
): Promise<{ success: boolean }> {
  const res = await tenantFetch('/tenant/move-out/feedback', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveOutId, feedback }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data;
}

export interface FurnitureSaleAction {
  step: string;
  status: 'completed' | 'skipped' | 'failed';
  detail: string;
}

export interface FurnitureSaleConsentResponse {
  furnitureSaleSellerConsent: boolean;
  actions?: FurnitureSaleAction[];
}

export async function registerFurnitureSaleSellerConsent(
  getToken: () => Promise<string>,
  moveOutId: string,
  consent: boolean
): Promise<FurnitureSaleConsentResponse> {
  const res = await tenantFetch('/tenant/move-out/furniture-interest', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveOutId, consent }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

// ---------------------------------------------------------------------------
// Checklist
// ---------------------------------------------------------------------------

export interface ChecklistAttachment {
  url: string;
  filename: string | null;
  mimeType: string | null;
  size: number | null;
}

export interface ChecklistItem {
  id: string;
  type: string;
  itemKey: string;
  label: string;
  category: string | null;
  completed: boolean;
  completedAt: string | null;
  sortOrder: number;
  metadata: Record<string, unknown> | null;
  attachment: ChecklistAttachment | null;
}

export interface ChecklistData {
  moveOutId: string;
  items: ChecklistItem[];
}

export async function getMoveOutChecklist(
  getToken: () => Promise<string>,
  moveOutId?: string
): Promise<ChecklistData> {
  const path = moveOutId
    ? `/tenant/move-out/${moveOutId}/checklist`
    : '/tenant/move-out/checklist';
  const res = await tenantFetch(path, getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function updateChecklistItem(
  getToken: () => Promise<string>,
  itemId: string,
  completed: boolean
): Promise<{ id: string; completed: boolean; completedAt: string | null }> {
  const res = await tenantFetch(`/tenant/move-out/checklist/${itemId}`, getToken, {
    method: 'POST',
    body: JSON.stringify({ completed }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function toggleChecklistItem(
  getToken: () => Promise<string>,
  itemKey: string,
  meta?: { type?: string; label?: string; category?: string; sortOrder?: number },
  moveOutId?: string
): Promise<{ action: 'checked' | 'unchecked'; itemKey: string; item?: ChecklistItem }> {
  const path = moveOutId
    ? `/tenant/move-out/${moveOutId}/checklist/toggle`
    : '/tenant/move-out/checklist/toggle';
  const res = await tenantFetch(path, getToken, {
    method: 'POST',
    body: JSON.stringify({ itemKey, ...meta }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function getChecklistUploadUrl(
  getToken: () => Promise<string>,
  itemId: string,
  filename: string,
  mimeType: string,
  size: number
): Promise<{ uploadUrl: string; s3Key: string; s3Bucket: string }> {
  const res = await tenantFetch(`/tenant/move-out/checklist/${itemId}/upload-url`, getToken, {
    method: 'POST',
    body: JSON.stringify({ filename, mimeType, size }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function confirmChecklistAttachment(
  getToken: () => Promise<string>,
  itemId: string,
  meta: { s3Key: string; s3Bucket: string; mimeType: string; size: number; filename: string }
): Promise<{ id: string; completed: boolean; completedAt: string | null; attachment: ChecklistAttachment }> {
  const res = await tenantFetch(`/tenant/move-out/checklist/${itemId}/attachment`, getToken, {
    method: 'POST',
    body: JSON.stringify(meta),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function deleteChecklistAttachment(
  getToken: () => Promise<string>,
  itemId: string
): Promise<{ id: string }> {
  const res = await tenantFetch(`/tenant/move-out/checklist/${itemId}/attachment`, getToken, {
    method: 'DELETE',
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

// ---------------------------------------------------------------------------
// Furniture Sale Items
// ---------------------------------------------------------------------------

export interface FurnitureSaleItem {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  mimeType: string;
  filename: string | null;
  size: number | null;
  imageUrl: string | null;
  createdAt: string;
}

export async function createFurnitureSaleItem(
  getToken: () => Promise<string>,
  moveOutId: string,
  name: string,
  file: File,
  description?: string
): Promise<{ item: FurnitureSaleItem; uploadUrl: string; s3Key: string }> {
  const res = await tenantFetch('/tenant/move-out/furniture-items', getToken, {
    method: 'POST',
    body: JSON.stringify({
      moveOutId,
      name,
      description: description || undefined,
      filename: file.name,
      mimeType: file.type,
      size: file.size,
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function getFurnitureSaleItems(
  getToken: () => Promise<string>,
  moveOutId?: string
): Promise<FurnitureSaleItem[]> {
  const path = moveOutId ? `/tenant/move-out/${moveOutId}/furniture-items` : '/tenant/move-out/furniture-items';
  const res = await tenantFetch(path, getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function updateFurnitureSaleItem(
  getToken: () => Promise<string>,
  itemId: string,
  updates: { name?: string; description?: string }
): Promise<{ id: string; name: string; description: string | null }> {
  const res = await tenantFetch(`/tenant/move-out/furniture-items/${itemId}`, getToken, {
    method: 'POST',
    body: JSON.stringify(updates),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function deleteFurnitureSaleItem(
  getToken: () => Promise<string>,
  itemId: string
): Promise<void> {
  const res = await tenantFetch(`/tenant/move-out/furniture-items/${itemId}`, getToken, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
}

// ---------------------------------------------------------------------------
// Furniture Sale — Proof of Purchase / Agreement
// ---------------------------------------------------------------------------

export interface FurnitureProof {
  filename: string;
  mimeType: string;
  size: number | null;
  downloadUrl: string;
}

export async function uploadFurnitureProof(
  getToken: () => Promise<string>,
  moveOutId: string,
  file: File
): Promise<FurnitureProof> {
  const urlRes = await tenantFetch('/tenant/move-out/furniture-proof/upload-url', getToken, {
    method: 'POST',
    body: JSON.stringify({
      moveOutId,
      filename: file.name,
      mimeType: file.type,
      size: file.size,
    }),
  });
  const urlData = await urlRes.json();
  if (!urlRes.ok) {
    throw new Error(urlData.message || urlData.error || `HTTP ${urlRes.status}`);
  }

  const { uploadUrl, s3Key, s3Bucket, mimeType, size, filename } = urlData.data;

  await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  const confirmRes = await tenantFetch('/tenant/move-out/furniture-proof', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveOutId, s3Key, s3Bucket, mimeType, size, filename }),
  });
  const confirmData = await confirmRes.json();
  if (!confirmRes.ok) {
    throw new Error(confirmData.message || confirmData.error || `HTTP ${confirmRes.status}`);
  }

  const proofRes = await tenantFetch('/tenant/move-out/furniture-proof', getToken);
  const proofData = await proofRes.json();
  if (!proofRes.ok) {
    throw new Error(proofData.message || proofData.error || `HTTP ${proofRes.status}`);
  }
  return proofData.data;
}

export async function getFurnitureProof(
  getToken: () => Promise<string>
): Promise<FurnitureProof | null> {
  const res = await tenantFetch('/tenant/move-out/furniture-proof', getToken);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data.data;
}

export async function deleteFurnitureProof(
  getToken: () => Promise<string>,
  moveOutId: string
): Promise<void> {
  const res = await tenantFetch('/tenant/move-out/furniture-proof', getToken, {
    method: 'DELETE',
    body: JSON.stringify({ moveOutId }),
  });
  if (!res.ok) {
    const data = await res.json();
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
}

// ===========================================================================
// MOVE-IN API
// ===========================================================================

export interface MoveInContentItem {
  id: string;
  sectionName: string;
  contentType: 'image' | 'video' | 'document';
  cloudfrontUrl: string;
  /** Secure proxy URL for react-pdf (PDFs only). */
  viewerUrl?: string | null;
  mimeType: string;
  altText: string | null;
  caption: string | null;
  displayOrder: number;
  s3Key?: string;
}

export async function getMoveInContent(
  getToken: () => Promise<string>
): Promise<MoveInContentItem[]> {
  const res = await tenantFetch('/tenant/move-in/content', getToken);
  const data: any = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export async function getMoveInChecklist(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<MoveInChecklistItem[]> {
  const res = await tenantFetch(`/tenant/move-in/${moveInId}/checklist`, getToken);
  const data: any = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export async function toggleMoveInChecklistItem(
  getToken: () => Promise<string>,
  moveInId: string,
  itemKey: string,
  completed: boolean
): Promise<void> {
  const res = await tenantFetch(`/tenant/move-in/${moveInId}/checklist/toggle`, getToken, {
    method: 'POST',
    body: JSON.stringify({ itemKey, completed }),
  });
  if (!res.ok) {
    const data: any = await res.json();
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
}

export async function getMoveInSlotsForDates(
  getToken: () => Promise<string>,
  moveInId: string,
  dates: string[]
): Promise<Record<string, MoveInSlotInfo[]>> {
  const res = await tenantFetch(
    `/tenant/move-in/${moveInId}/slots?dates=${encodeURIComponent(dates.join(','))}`,
    getToken
  );
  const data: any = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data.slotsByDate;
}

export async function bookMoveInSlot(
  getToken: () => Promise<string>,
  moveInId: string,
  startTime: string
): Promise<void> {
  const res = await tenantFetch('/tenant/move-in/book', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveInId, startTime }),
  });
  if (!res.ok) {
    const data: any = await res.json();
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
}

export async function confirmMoveInScheduledDate(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<void> {
  const res = await tenantFetch('/tenant/move-in/confirm', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveInId }),
  });
  if (!res.ok) {
    const data: any = await res.json();
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
}

export async function getMoveInTenantsInsurance(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<{ tenants: MoveInTenantInsurance[]; allComplete: boolean; currentUserTenantId: number | null }> {
  const res = await tenantFetch(`/tenant/move-in/${moveInId}/tenants-insurance`, getToken);
  const data: any = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export async function uploadInsuranceDocument(
  getToken: () => Promise<string>,
  file: File,
  moveInId: string
): Promise<{ tenantId: number; hasInsuranceDocument: boolean; insuranceConfirmed: boolean }> {
  const urlRes = await tenantFetch('/tenant/move-in/insurance/upload-url', getToken, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, mimeType: file.type, size: file.size, moveInId }),
  });
  const urlData = await urlRes.json();
  if (!urlRes.ok) throw new Error(urlData.message || urlData.error || `HTTP ${urlRes.status}`);

  const { uploadUrl, s3Key } = urlData.data;

  await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  const confirmRes = await tenantFetch('/tenant/move-in/insurance/confirm', getToken, {
    method: 'POST',
    body: JSON.stringify({ s3Key, moveInId }),
  });
  const confirmData = await confirmRes.json();
  if (!confirmRes.ok) throw new Error(confirmData.message || confirmData.error || `HTTP ${confirmRes.status}`);

  return confirmData.data;
}

export async function deleteInsuranceDocument(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<{ tenantId: number; hasInsuranceDocument: boolean; insuranceConfirmed: boolean }> {
  const res = await tenantFetch('/tenant/move-in/insurance/delete', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveInId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

// ---------------------------------------------------------------------------
// Accessory Insurance Documents (Document model, type TENANT_INSURANCE)
// ---------------------------------------------------------------------------

export interface AccessoryInsuranceDoc {
  id: string;
  name: string;
  mimeType: string | null;
  size: number | null;
  downloadUrl: string | null;
  uploadedAt: string;
}

export async function getAccessoryInsuranceDocs(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<AccessoryInsuranceDoc[]> {
  const res = await tenantFetch(`/tenant/move-in/${moveInId}/insurance-accessory`, getToken);
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export async function uploadAccessoryInsuranceDoc(
  getToken: () => Promise<string>,
  file: File,
  moveInId: string,
  label: string
): Promise<AccessoryInsuranceDoc> {
  const urlRes = await tenantFetch('/tenant/move-in/insurance-accessory/upload-url', getToken, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, mimeType: file.type, size: file.size, moveInId, label }),
  });
  const urlData = await urlRes.json();
  if (!urlRes.ok) throw new Error(urlData.message || urlData.error || `HTTP ${urlRes.status}`);

  const { uploadUrl, s3Key, docLabel } = urlData.data;

  await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  const confirmRes = await tenantFetch('/tenant/move-in/insurance-accessory/confirm', getToken, {
    method: 'POST',
    body: JSON.stringify({ s3Key, moveInId, docLabel, mimeType: file.type, size: file.size }),
  });
  const confirmData = await confirmRes.json();
  if (!confirmRes.ok) throw new Error(confirmData.message || confirmData.error || `HTTP ${confirmRes.status}`);

  return confirmData.data;
}

export async function deleteAccessoryInsuranceDoc(
  getToken: () => Promise<string>,
  moveInId: string,
  documentId: string
): Promise<void> {
  const res = await tenantFetch('/tenant/move-in/insurance-accessory/delete', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveInId, documentId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
}

export async function getMoveInUtilitiesStatus(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<MoveInUtilitiesStatus> {
  const res = await tenantFetch(`/tenant/move-in/${moveInId}/utilities-status`, getToken);
  const data: any = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export async function uploadUtilitiesDocument(
  getToken: () => Promise<string>,
  file: File,
  moveInId: string
): Promise<{ tenantId: number; hasUtilitiesDocument: boolean }> {
  const urlRes = await tenantFetch('/tenant/move-in/utilities/upload-url', getToken, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, mimeType: file.type, size: file.size, moveInId }),
  });
  const urlData = await urlRes.json();
  if (!urlRes.ok) throw new Error(urlData.message || urlData.error || `HTTP ${urlRes.status}`);

  const { uploadUrl, s3Key } = urlData.data;

  await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  const confirmRes = await tenantFetch('/tenant/move-in/utilities/confirm', getToken, {
    method: 'POST',
    body: JSON.stringify({ s3Key, moveInId }),
  });
  const confirmData = await confirmRes.json();
  if (!confirmRes.ok) throw new Error(confirmData.message || confirmData.error || `HTTP ${confirmRes.status}`);

  return confirmData.data;
}

export async function deleteUtilitiesDocument(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<{ tenantId: number; hasUtilitiesDocument: boolean }> {
  const res = await tenantFetch('/tenant/move-in/utilities/delete', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveInId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export async function uploadUtilitiesEnrollmentForm(
  getToken: () => Promise<string>,
  file: File,
  moveInId: string
): Promise<{ tenantId: number; hasEnrollmentFormDocument: boolean }> {
  const urlRes = await tenantFetch('/tenant/move-in/utilities-enrollment/upload-url', getToken, {
    method: 'POST',
    body: JSON.stringify({ filename: file.name, mimeType: file.type, size: file.size, moveInId }),
  });
  const urlData = await urlRes.json();
  if (!urlRes.ok) throw new Error(urlData.message || urlData.error || `HTTP ${urlRes.status}`);

  const { uploadUrl, s3Key } = urlData.data;

  await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  const confirmRes = await tenantFetch('/tenant/move-in/utilities-enrollment/confirm', getToken, {
    method: 'POST',
    body: JSON.stringify({ s3Key, moveInId }),
  });
  const confirmData = await confirmRes.json();
  if (!confirmRes.ok) throw new Error(confirmData.message || confirmData.error || `HTTP ${confirmRes.status}`);

  return confirmData.data;
}

export async function deleteUtilitiesEnrollmentForm(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<{ tenantId: number; hasEnrollmentFormDocument: boolean }> {
  const res = await tenantFetch('/tenant/move-in/utilities-enrollment/delete', getToken, {
    method: 'POST',
    body: JSON.stringify({ moveInId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export async function getMoveInRentSplit(
  getToken: () => Promise<string>,
  moveInId: string
): Promise<MoveInRentSplitData> {
  const res = await tenantFetch(`/tenant/move-in/${moveInId}/rent-split`, getToken);
  const data: any = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export async function getMoveInUnitRoommates(
  getToken: () => Promise<string>,
  moveInId: string,
): Promise<MoveInUnitRoommatesData> {
  const res = await tenantFetch(`/tenant/move-in/${moveInId}/unit-roommates`, getToken);
  const data: any = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data;
}

export type ProspectIncomeKind = 'OSAP' | 'PARENT_CONTRIBUTION' | 'EMPLOYMENT' | 'SCHOLARSHIP' | 'SAVINGS' | 'OTHER';
export type ProspectSupplementalSuggestion = 'CURRENT_HOME_PHOTO' | 'PERSONAL_BIO' | 'COVER_LETTER' | 'OSAP_FUNDING' | 'OTHER';
export type ProspectSubmissionOutcome = 'PENDING' | 'ACCEPTED' | 'DENIED';
export type ProspectApplicationDocumentKind = 'photo-id' | 'headshot' | 'supplemental';
export type ProspectGovernmentIdKind = 'PASSPORT' | 'DRIVERS_LICENSE' | 'ID_CARD';

export interface ProspectApplicationReference {
  id: string;
  name: string;
  relationship: string;
  phone: string;
  email: string;
  callStartTime: string;
  callEndTime: string;
  note: string | null;
  sortOrder: number;
}

export interface ProspectApplicationIncome {
  id: string;
  kind: ProspectIncomeKind;
  osapCategory: string;
  label: string;
  employerName: string;
  rentSharePercent: number | null;
  details: string | null;
  sortOrder: number;
}

export interface ProspectApplicationFile {
  id: string;
  documentId: string;
  name: string;
  mimeType: string;
  suggestion: ProspectSupplementalSuggestion | null;
  caption: string | null;
  sortOrder: number;
}

export interface ProspectApplicationProgress {
  identity: boolean;
  birthday: boolean;
  address: boolean;
  emergencyContact: boolean;
  income: boolean;
  photoId: boolean;
  headshot: boolean;
  readyToSubmit: boolean;
}

export interface ProspectApplicationPerson {
  applicantId: string;
  personId: string;
  displayName: string;
  isViewer: boolean;
  firstName: string;
  lastName: string;
  preferredFirstName: string;
  phone: string;
  email: string;
  dateOfBirth: string | null;
  address: string;
  city: string;
  unitNumber: string;
  province: string;
  postalCode: string;
  country: string;
  school: string;
  program: string;
  currentYearOfStudy: string;
  expectedGraduationTerm: string;
  expectedGraduationYear: string;
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  emergencyContactEmail: string;
  hasPhotoId: boolean;
  hasHeadshot: boolean;
  academicChecks: {
    year: boolean;
    faculty: boolean;
    program: boolean;
    graduation: boolean;
  };
  taskChecks: ApplicationTaskChecks;
  referenceCount: number;
  supplementalCount: number;
  photoIdDocumentId: string | null;
  photoIdDocumentName: string | null;
  headshotDocumentId: string | null;
  references: ProspectApplicationReference[];
  incomeSources: ProspectApplicationIncome[];
  supplementalDocuments: ProspectApplicationFile[];
  progress: ProspectApplicationProgress;
}

export interface ProspectApplicationHome {
  unitId: string;
  label: string;
  preferenceOrder: number | null;
  shortlistRank: number | null;
  tourComplete: boolean;
  linked: boolean;
  /** Listed Hive suite surfaced after a building tour (not on shortlist). */
  hiveListedUnit: boolean;
  propertyId: string | null;
  latitude: number | null;
  longitude: number | null;
  submissions: Array<{ id: string; submittedAt: string; outcome: ProspectSubmissionOutcome }>;
}

export interface ProspectApplicationPage {
  open: boolean;
  reason: string | null;
  applicationId: string | null;
  status: string | null;
  submittedAt: string | null;
  denialCount: number;
  viewerPersonId: string;
  characterReferencesRequired: boolean;
  applicants: ProspectApplicationPerson[];
  homes: ProspectApplicationHome[];
  referralName: string;
  referralSource: string | null;
}

async function readApplicationPage(res: Response): Promise<ProspectApplicationPage> {
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data as ProspectApplicationPage;
}

export async function getProspectApplication(getToken: () => Promise<string>): Promise<ProspectApplicationPage> {
  const res = await tenantFetch('/tenant/prospect/application', getToken);
  return readApplicationPage(res);
}

export interface ProspectApplicationChecks {
  applicants: Array<{
    applicantId: string;
    displayName: string;
    hasPhotoId: boolean;
    hasHeadshot: boolean;
    headshotDocumentId: string | null;
    academicChecks: ProspectApplicationPerson['academicChecks'];
    taskChecks: ApplicationTaskChecks;
    referenceCount: number;
    supplementalCount: number;
  }>;
  linkedUnitIds: string[];
}

export async function getProspectApplicationChecks(getToken: () => Promise<string>): Promise<ProspectApplicationChecks> {
  const res = await tenantFetch('/tenant/prospect/application/checks', getToken);
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data as ProspectApplicationChecks;
}

export async function saveProspectReferral(
  getToken: () => Promise<string>,
  payload: { referrerName: string; referrerSource?: string | null },
  options?: { keepalive?: boolean },
): Promise<{ referrerName: string; referrerSource: string | null }> {
  const res = await tenantFetch('/tenant/prospect/application/referral', getToken, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    keepalive: options?.keepalive,
  });
  const data = await res.json().catch(() => null) as {
    message?: string;
    error?: string;
    data?: { referrerName: string; referrerSource: string | null };
  } | null;
  if (!res.ok) throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
  return data?.data ?? { referrerName: payload.referrerName, referrerSource: payload.referrerSource ?? null };
}

export async function saveProspectApplicant(
  getToken: () => Promise<string>,
  patch: Partial<ProspectApplicationPerson>,
  options?: { keepalive?: boolean },
): Promise<void> {
  const res = await tenantFetch('/tenant/prospect/application/applicant', getToken, {
    method: 'PATCH',
    body: JSON.stringify(patch),
    keepalive: options?.keepalive,
  });
  const data = await res.json().catch(() => null) as { message?: string; error?: string } | null;
  if (!res.ok) throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
}

export async function addProspectReference(
  getToken: () => Promise<string>,
  reference: { name: string; relationship: string; phone: string; callStartTime: string; callEndTime: string; note?: string },
): Promise<ProspectApplicationPage> {
  const res = await tenantFetch('/tenant/prospect/application/references', getToken, {
    method: 'POST',
    body: JSON.stringify(reference),
  });
  return readApplicationPage(res);
}

export async function removeProspectReference(
  getToken: () => Promise<string>,
  referenceId: string,
): Promise<ProspectApplicationPage> {
  const res = await tenantFetch(`/tenant/prospect/application/references/${referenceId}`, getToken, { method: 'DELETE' });
  return readApplicationPage(res);
}

export async function addProspectIncome(
  getToken: () => Promise<string>,
  income: {
    kind: ProspectIncomeKind;
    osapCategory?: string;
    label?: string;
    employerName?: string;
    rentSharePercent: number;
  },
): Promise<ProspectApplicationPage> {
  const res = await tenantFetch('/tenant/prospect/application/income', getToken, {
    method: 'POST',
    body: JSON.stringify(income),
  });
  return readApplicationPage(res);
}

export async function updateProspectIncome(
  getToken: () => Promise<string>,
  incomeId: string,
  income: {
    kind: ProspectIncomeKind;
    osapCategory?: string;
    label?: string;
    employerName?: string;
    rentSharePercent: number;
  },
): Promise<ProspectApplicationPage> {
  const res = await tenantFetch(`/tenant/prospect/application/income/${incomeId}`, getToken, {
    method: 'PATCH',
    body: JSON.stringify(income),
  });
  return readApplicationPage(res);
}

export async function removeProspectIncome(
  getToken: () => Promise<string>,
  incomeId: string,
): Promise<ProspectApplicationPage> {
  const res = await tenantFetch(`/tenant/prospect/application/income/${incomeId}`, getToken, { method: 'DELETE' });
  return readApplicationPage(res);
}

export async function addProspectApplicationUnit(
  getToken: () => Promise<string>,
  unitId: string,
): Promise<ProspectApplicationPage> {
  const res = await tenantFetch('/tenant/prospect/application/units', getToken, {
    method: 'POST',
    body: JSON.stringify({ unitId }),
  });
  return readApplicationPage(res);
}

export async function removeProspectApplicationUnit(
  getToken: () => Promise<string>,
  unitId: string,
): Promise<ProspectApplicationPage> {
  const res = await tenantFetch(`/tenant/prospect/application/units/${unitId}`, getToken, { method: 'DELETE' });
  return readApplicationPage(res);
}

export async function uploadProspectApplicationFile(
  getToken: () => Promise<string>,
  file: File,
  documentType: ProspectApplicationDocumentKind,
  extra?: { suggestion?: ProspectSupplementalSuggestion; caption?: string; idKind?: ProspectGovernmentIdKind; documentName?: string },
): Promise<ProspectApplicationPage> {
  const start = await tenantFetch('/tenant/prospect/application/upload-url', getToken, {
    method: 'POST',
    body: JSON.stringify({
      documentType,
      filename: file.name,
      mimeType: file.type,
      idKind: extra?.idKind,
      suggestion: extra?.suggestion,
      documentName: extra?.documentName,
    }),
  });
  const started = await start.json();
  if (!start.ok) throw new Error(started.message || started.error || 'Unable to start that upload.');
  const uploaded = await fetch(started.data.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!uploaded.ok) throw new Error('The file did not upload.');
  const saved = await tenantFetch('/tenant/prospect/application/documents', getToken, {
    method: 'POST',
    body: JSON.stringify({
      documentType,
      s3Key: started.data.s3Key,
      filename: file.name,
      mimeType: file.type,
      size: file.size,
      suggestion: extra?.suggestion,
      caption: extra?.caption,
      idKind: extra?.idKind,
      documentName: extra?.documentName,
    }),
  });
  return readApplicationPage(saved);
}

export async function fetchProspectApplicationFile(
  getToken: () => Promise<string>,
  documentId: string,
): Promise<Blob> {
  const res = await tenantFetch(`/tenant/prospect/application/documents/${documentId}/file`, getToken);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || data.error || 'Unable to open that file.');
  }
  return res.blob();
}

export async function removeProspectApplicationDocument(
  getToken: () => Promise<string>,
  documentId: string,
): Promise<ProspectApplicationPage> {
  const res = await tenantFetch(`/tenant/prospect/application/documents/${documentId}`, getToken, { method: 'DELETE' });
  return readApplicationPage(res);
}

export async function openProspectApplicationDocument(
  getToken: () => Promise<string>,
  documentId: string,
): Promise<string> {
  const res = await tenantFetch(`/tenant/prospect/application/documents/${documentId}/url`, getToken);
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || 'Unable to open that file.');
  return data.data.url as string;
}

export type ProspectSelectionStatus = 'pending' | 'selected' | 'not_selected';

export interface ProspectSelectionHome {
  unitId: string;
  label: string;
  availableDate: string | null;
  submittedAt: string;
  status: ProspectSelectionStatus;
}

export interface ProspectSelection {
  homes: ProspectSelectionHome[];
  selectedUnitId: string | null;
  leaseSigned: boolean;
}

export async function getProspectSelection(getToken: () => Promise<string>): Promise<ProspectSelection> {
  const res = await tenantFetch('/tenant/prospect/selection', getToken);
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data.data as ProspectSelection;
}

export async function submitProspectApplication(getToken: () => Promise<string>): Promise<ProspectApplicationPage> {
  const res = await tenantFetch('/tenant/prospect/application/submit', getToken, { method: 'POST' });
  return readApplicationPage(res);
}

export async function saveMoveInRentSplit(
  getToken: () => Promise<string>,
  moveInId: string,
  splits: Array<{ tenantId: number; amount: number }>
): Promise<void> {
  const res = await tenantFetch(`/tenant/move-in/${moveInId}/rent-split`, getToken, {
    method: 'POST',
    body: JSON.stringify({ splits }),
  });
  if (!res.ok) {
    const data: any = await res.json();
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
}
